import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EditFieldDialog } from "@/components/EditFieldDialog";
import { RecordPanel } from "@/components/record/RecordFrame";

interface PastAcquisition { id: string; acquired_company_name: string; industry: string | null; closed_date: string | null }

/** The buyer's past acquisitions: twelve, then "Show all". Add by name; remove with a confirm. */
export function PortfolioPanel({ companyId }: { companyId: string }) {
  const qc = useQueryClient();
  const key = ["past-acquisitions", companyId];
  const [adding, setAdding] = useState(false);
  const [pending, setPending] = useState<PastAcquisition | null>(null);
  const [showAll, setShowAll] = useState(false);

  const { data: rows = [] } = useQuery<PastAcquisition[]>({
    queryKey: key,
    queryFn: async () => {
      const r = await fetch(`/api/companies/${companyId}/past-acquisitions`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load portfolio");
      return r.json();
    },
  });

  const remove = useMutation({
    mutationFn: async (row: PastAcquisition) => {
      const r = await fetch(`/api/past-acquisitions/${row.id}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error(`Couldn't remove ${row.acquired_company_name}`);
      return row;
    },
    onSuccess: (row) => { qc.invalidateQueries({ queryKey: key }); setPending(null); toast.success(`Removed ${row.acquired_company_name}`); },
    onError: (e: Error) => toast.error(e.message),
  });

  const shown = showAll ? rows : rows.slice(0, 12);
  return (
    <RecordPanel
      title="Portfolio"
      count={rows.length}
      action={
        <button type="button" onClick={() => setAdding(true)} className="inline-flex items-center gap-1 text-xs font-semibold text-primary-text hover:underline">
          <Plus className="h-3 w-3" />Add
        </button>
      }
    >
      {rows.length === 0 ? (
        <p className="text-[12.5px] text-muted-foreground">No portfolio companies yet. Enrich from the website or a one-pager to fill these in.</p>
      ) : (
        <ul className="grid gap-x-4 sm:grid-cols-2">
          {shown.map((pa) => (
            <li key={pa.id} className="group flex items-center justify-between gap-2 border-b py-1 text-[12.5px]">
              <span className="min-w-0 truncate font-semibold">{pa.acquired_company_name}</span>
              <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                {/* The year is read from the stored day; new Date() would put Jan 1 in the year before, west of UTC. */}
                {[pa.industry, pa.closed_date ? pa.closed_date.slice(0, 4) : null].filter(Boolean).join(" · ")}
                <button
                  type="button"
                  aria-label={`Remove ${pa.acquired_company_name}`}
                  onClick={() => setPending(pa)}
                  className="opacity-0 transition-opacity hover:text-destructive focus:opacity-100 group-hover:opacity-100"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {rows.length > shown.length && (
        <button type="button" onClick={() => setShowAll(true)} className="mt-1.5 text-xs font-semibold text-primary-text hover:underline">
          Show all {rows.length}
        </button>
      )}
      <EditFieldDialog
        open={adding}
        onOpenChange={setAdding}
        title="Add portfolio company"
        label="Company name"
        saveLabel="Add"
        onSave={async (name) => {
          if (!name) return false;
          const r = await fetch(`/api/companies/${companyId}/past-acquisitions`, {
            method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ acquiredCompanyName: name }),
          });
          if (!r.ok) throw new Error("Couldn't add it. Try again.");
          qc.invalidateQueries({ queryKey: key });
          toast.success(`Added ${name} to portfolio`);
        }}
      />
      <ConfirmDialog
        open={!!pending}
        onOpenChange={(o) => { if (!o) setPending(null); }}
        title={`Remove ${pending?.acquired_company_name ?? "this company"}?`}
        description="It comes off this buyer's portfolio list. Nothing else changes."
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="destructive"
        isLoading={remove.isPending}
        onConfirm={() => { if (pending) remove.mutate(pending); }}
      />
    </RecordPanel>
  );
}
